import { useCallback, useMemo } from 'react';
import { useSelectedVersion } from '@client/store/ui';
import {
  RawProjectParamsObject,
  useRawProjectParams,
} from './use-router.hooks';
import { useSearchParam } from './use-search-param.hook';
import {
  getElementById,
  getPathToElement,
  isElement,
} from '@shared/helpers/recursive-element.helpers';
import { IElement, OneOfListElements } from '@shared/models/project.model';
import { getElementName } from '@shared/helpers/element.helpers';
import { generatePath, useNavigate, useSearchParams } from 'react-router-dom';
import {
  NODON_PROJECT_PATH_STRICT,
  NODON_SHARED_PROJECT_PATH,
} from '@shared/constants';
import {
  SELECTED_ELEMENT_SEARCH_PARAM,
  URL_CATEGORY_NONE,
} from '@client/models/router.model';
import {
  getElementGroupingHelpers,
  GroupSectionMeta,
} from '@features/elements/element-grouping.model';
import { useElementListGroupBy } from '@features/elements/helpers/element-grouping.helpers';
import { getElementIconCategoryName } from '@features/elements/helpers/get-element-category-icon-name.helper';
import { getElementCategory } from '@shared/helpers/element-category.helpers';
import { icons } from '@ui/Icon';
import { GroupSectionData } from '@ui/ElementList/list.model';
import { useGroupSectionProps } from '@client/pages/MainView/components/ProjectView/components/ElementsView/element-groups.model';
import { useFilteredElementChildren } from './use-filter-elements.hooks';
import {
  normalizeSbefScopeGroupKey,
  SITE_OPERATIONS_SBEF_GROUP_KEY,
} from '@shared/helpers/sbef-grouping.helpers';
import { escapeRegExp } from 'lodash';

const FOCUS_ELEMENT_SEARCH_PARAM = 'focusElementId';
const FOCUS_GROUP_KEY_SEARCH_PARAM = 'focusGroupKey';

type FocusSearchMergeMode =
  | { type: 'element'; elementId: string }
  | { type: 'group'; groupId: string }
  | { type: 'clearFocus' };

/**
 * Returns a `?…` search string: applies focus param changes on a copy of
 * {@link baseSearchParams} so unrelated keys (e.g. `proposal=`) survive navigation.
 * Also carries the selected element over to `?selected=` (DEV-3454).
 */
const mergeFocusSearchParams = (
  baseSearchParams: URLSearchParams,
  mode: FocusSearchMergeMode,
  selectedElementId: string,
): string => {
  const next = new URLSearchParams(baseSearchParams);
  if (selectedElementId && selectedElementId !== URL_CATEGORY_NONE) {
    next.set(SELECTED_ELEMENT_SEARCH_PARAM, selectedElementId);
  } else {
    next.delete(SELECTED_ELEMENT_SEARCH_PARAM);
  }
  if (mode.type === 'element') {
    next.set(FOCUS_ELEMENT_SEARCH_PARAM, mode.elementId);
    next.delete(FOCUS_GROUP_KEY_SEARCH_PARAM);
  } else if (mode.type === 'group') {
    next.set(FOCUS_GROUP_KEY_SEARCH_PARAM, mode.groupId);
    next.delete(FOCUS_ELEMENT_SEARCH_PARAM);
  } else {
    next.delete(FOCUS_ELEMENT_SEARCH_PARAM);
    next.delete(FOCUS_GROUP_KEY_SEARCH_PARAM);
  }
  const qs = next.toString();
  return qs ? `?${qs}` : '';
};
const toUppercaseLabel = (value: string): string => value.toUpperCase();
const formatGroupLabel = ({
  groupKey,
  fallbackLabel,
  customLabel,
}: {
  groupKey: string;
  fallbackLabel: string;
  customLabel?: string;
}): string => {
  const normalizedKey = normalizeSbefScopeGroupKey(groupKey);
  if (!customLabel) return toUppercaseLabel(fallbackLabel);
  if (normalizedKey === SITE_OPERATIONS_SBEF_GROUP_KEY) {
    const keyPrefixRegExp = /^9(?:\.|\s)/i;
    const hasKeyPrefix = keyPrefixRegExp.test(customLabel.trim());
    return toUppercaseLabel(hasKeyPrefix ? customLabel : `9. ${customLabel}`);
  }

  const keyPrefixRegExp = new RegExp(
    `^${escapeRegExp(normalizedKey)}(?:\\.|\\s)`,
    'i',
  );
  const hasKeyPrefix = keyPrefixRegExp.test(customLabel.trim());
  const label = hasKeyPrefix ? customLabel : `${normalizedKey}. ${customLabel}`;
  return toUppercaseLabel(label);
};

export const useFocusElementId = () =>
  useSearchParam(FOCUS_ELEMENT_SEARCH_PARAM, '');

export const useFocusGroupId = () =>
  useSearchParam(FOCUS_GROUP_KEY_SEARCH_PARAM, '');

export const useOpenFocusElement = () => {
  const navigate = useNavigate();
  const projectParams = useRawProjectParams();
  const [searchParams] = useSearchParams();

  return useCallback(
    (elementId: string) => {
      const path = getElementListPath(projectParams);
      if (!path) return;
      navigate(
        path +
          mergeFocusSearchParams(
            searchParams,
            { type: 'element', elementId },
            elementId,
          ),
      );
    },
    [navigate, projectParams, searchParams],
  );
};

export const useOpenFocusGroup = () => {
  const projectParams = useRawProjectParams();
  const focusElement = useFocusElement();
  const elementId = focusElement?.id ?? 'none';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  return useCallback(
    (groupId: string) => {
      const path = getElementListPath(projectParams);
      if (!path) return;
      navigate(
        path +
          mergeFocusSearchParams(
            searchParams,
            { type: 'group', groupId },
            elementId,
          ),
      );
    },
    [navigate, projectParams, elementId, searchParams],
  );
};

export const useFocusViewBackNavigation = () => {
  const [focusGroupId, setFocusGroupId] = useFocusGroupId();
  const focusElement = useFocusElement();
  const projectParams = useRawProjectParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  return useCallback(() => {
    if (focusElement) {
      const path = getElementListPath(projectParams);
      if (!path) return;
      navigate(
        path +
          mergeFocusSearchParams(
            searchParams,
            { type: 'clearFocus' },
            focusElement.id,
          ),
      );
      return;
    }
    if (focusGroupId) setFocusGroupId('');
  }, [
    focusElement,
    focusGroupId,
    projectParams,
    navigate,
    setFocusGroupId,
    searchParams,
  ]);
};

export const useFocusElement = (): IElement | undefined => {
  const [focusElementId] = useFocusElementId();
  const version = useSelectedVersion();

  return useMemo(() => {
    const element = getElementById(version, focusElementId);
    return isElement(element) ? element : undefined;
  }, [focusElementId, version]);
};

export const useFocusGroup = ():
  GroupSectionData<OneOfListElements, GroupSectionMeta> | undefined => {
  const [focusGroupId] = useFocusGroupId();
  const [groupBy] = useElementListGroupBy();
  const groupProps = useGroupSectionProps(groupBy);
  const rootChildren = useFilteredElementChildren(undefined);

  const isGroupFocus = Boolean(
    focusGroupId && groupBy === 'sbef' && groupProps,
  );
  return useMemo(() => {
    if (!isGroupFocus || !groupProps) return undefined;
    const sections = groupProps.groupSectionBuilder(rootChildren);

    return sections.find((s) => s.key === focusGroupId);
  }, [isGroupFocus, groupProps, rootChildren, focusGroupId]);
};

export const useFocusViewItems = (): OneOfListElements[] => {
  const [focusGroupId] = useFocusGroupId();
  const [groupBy] = useElementListGroupBy();
  const focusElement = useFocusElement();
  const groupProps = useGroupSectionProps(groupBy);
  const elementChildren = useFilteredElementChildren(focusElement);
  const focusedGroup = useFocusGroup();

  const isGroupFocus = Boolean(
    focusGroupId && groupBy === 'sbef' && groupProps,
  );

  if (!focusElement && isGroupFocus && focusedGroup) {
    return focusedGroup.items;
  }
  return elementChildren;
};

export type FocusViewSegment = {
  type: 'group' | 'element';
  label: string;
  id: string;
  icon?: keyof (typeof icons)['elementCategory'];
  disabled?: boolean;
};

export const useFocusViewSegments = (): FocusViewSegment[] => {
  const [groupBy] = useElementListGroupBy();
  const version = useSelectedVersion();
  const focusElement = useFocusElement();
  const focusedGroup = useFocusGroup();

  const segments = useMemo((): FocusViewSegment[] => {
    const focusedGroupCustomLabel =
      focusedGroup && version
        ? version.sbefGroupNames?.[focusedGroup.key]
        : undefined;
    const group: FocusViewSegment | undefined = focusedGroup
      ? {
          type: 'group' as const,
          id: focusedGroup.key,
          label: formatGroupLabel({
            groupKey: focusedGroup.key,
            fallbackLabel: focusedGroup.label,
            customLabel: focusedGroupCustomLabel,
          }),
          disabled: focusElement === undefined,
        }
      : undefined;

    if (!version || !focusElement) {
      return group ? [group] : [];
    }

    const path = getPathToElement(version, focusElement.id, false);
    if (!path?.length) return [];

    const elementPath = [...path.filter(isElement), focusElement];

    const groupHelpers =
      groupBy === 'sbef'
        ? getElementGroupingHelpers('sbef')?.helpers
        : undefined;

    const firstElement = elementPath[0];

    const groupSegment: FocusViewSegment | undefined =
      groupHelpers && firstElement
        ? {
            type: 'group' as const,
            id: groupHelpers.getGroupKey(firstElement),
            label: formatGroupLabel({
              groupKey: groupHelpers.getGroupKey(firstElement),
              fallbackLabel: groupHelpers.getGroupLabel(
                groupHelpers.getGroupKey(firstElement),
              ),
              customLabel:
                version.sbefGroupNames?.[
                  groupHelpers.getGroupKey(firstElement)
                ],
            }),
            disabled: focusElement === undefined,
          }
        : undefined;

    const elementSegments: FocusViewSegment[] = [
      ...elementPath.map((el) => {
        const category = getElementCategory(el);
        const icon = category && getElementIconCategoryName(category.id);
        const label = getElementName(el);

        return {
          type: 'element' as const,
          id: el.id,
          label,
          icon,
        };
      }),
    ];

    return groupSegment ? [groupSegment, ...elementSegments] : elementSegments;
  }, [focusedGroup, focusElement, version, groupBy]);

  return useMemo(() => segments, [segments]);
};

const getElementListPath = (params: RawProjectParamsObject): string | null => {
  if (params.type === 'project' && params.data) {
    const d = params.data;
    return generatePath(NODON_PROJECT_PATH_STRICT, {
      organization: d.organization ?? null,
      projectId: d.projectId ?? null,
      versionId: d.versionId ?? null,
      page: d.page ?? null,
    });
  }
  if (params.type === 'shared' && params.data) {
    const d = params.data;
    const sharingKey = d.sharingKey;
    const versionId = d.versionId;
    // Required URL segments — cannot build path without both
    if (!sharingKey || !versionId) return null;
    return generatePath(NODON_SHARED_PROJECT_PATH, {
      sharingKey,
      versionId,
      page: d.page ?? null,
    });
  }
  return null;
};
